/**
 * This really should be `React.Key` but
 * Checkbox component enforces `string | number`
 * and in order to keep the types consistent
 * across the library, we use the same type here.
 */
export type ListValue = string | number;

/**
 * Optional header inside a group.
 */
export type HeaderListItem = {
  label: string;
  isHeader: true;
  icon?: React.ComponentType;
};

export const ListItemVariant = {
  Default: 'default',
  Destructive: 'destructive',
} as const;
export type ListItemVariant = (typeof ListItemVariant)[keyof typeof ListItemVariant];

/**
 * Selectable item inside a group.
 */
export type ValueListItem = {
  label: string;
  value: ListValue;
  icon?: React.ComponentType;
  itemCallback?: (value: ListValue) => void;
  /**
   * This is auto created using the builder.
   * If you manually the list object, the focusId must follow the same pattern.
   * e.g. `focusable-item-${value}-${id}`
   * This is used for keyboard navigation and focus management.
   */
  focusId: string; // Unique identifier for focus management
  variant?: ListItemVariant;
};

/**
 * Items allowed inside a group: a header or a value.
 */
export type ListItem = HeaderListItem | ValueListItem;

export type EnforcedListItems = [ListItem, ...ListItem[]];
export type EnforcedGroupListItems = [GroupListItem, ...GroupListItem[]];

/**
 * A group must contain at least one item.
 */
export type GroupListItem = {
  /** Group items (headers and values) */
  items: EnforcedListItems; // at least one item (header or value)

  /** Map of value to ValueListItem for quick lookup */
  valueItemMap: Map<ListValue, ValueListItem>;
};

/**
 * MultiSelectList must have at least one group.
 */
export type StatefulList = {
  /** List unique identifier */
  id: string;
  /** Item group enforced to contain at least one group */
  groups: EnforcedGroupListItems;

  /** Map of value to ValueListItem for quick lookup */
  valueItemMap: Map<ListValue, ValueListItem>;
};

export type FocusableItem = {
  id: string;
  value: ListValue | ListReservedValue;
  onItemSelect?: (item: ListValue | ListReservedValue) => void;
};

export const ListReservedValues = {
  selectAll: -999999,
  search: -999998,
  noResult: -999997,
} as const;
export type ListReservedValue =
  (typeof ListReservedValues)[keyof typeof ListReservedValues];

export type KeyboardNavigation = {
  activeDescendantId: string | undefined;
  containerRef: React.RefObject<HTMLDivElement>;
  onKeyDown: (event: React.KeyboardEvent) => void;
};

export const KeyboardNavigationKey = {
  Tab: 'Tab',
  ArrowDown: 'ArrowDown',
  ArrowUp: 'ArrowUp',
  Enter: 'Enter',
  Space: ' ',
} as const;
export type KeyboardNavigationKey =
  (typeof KeyboardNavigationKey)[keyof typeof KeyboardNavigationKey];
